import type { BinaryOp, Expr, Value } from "@package/scratchpad-utils";

export class FormulaError extends Error {
  code: string;
  constructor(code: string, message?: string) {
    super(message ?? code);
    this.code = code;
  }
}

export type Arg = { eval: () => Value; values: () => Value[] };
export type FnContext = { external: (key: string) => Value | null | undefined };
export type Fn = (args: Arg[], fnCtx: FnContext) => Value;
export type FnValues = (args: Arg[], fnCtx: FnContext) => Array<Value | null>;
export type FunctionDef = { fn: Fn; values?: FnValues; params: readonly string[]; description: string };
export type FunctionRegistry = Record<string, FunctionDef>;

export type EvalContext = {
  sheet?: string;
  resolveCell: (sheet: string | undefined, col: number, row: number) => Value | null;
  resolveExternal?: (key: string) => Value | null | undefined;
  trackExternal?: (key: string) => void;
  functions: FunctionRegistry;
};

const MAX_COL = 16384;
const MAX_ROW = 1_048_576;

function checkBounds(col: number, row: number): void {
  if (col < 0 || col >= MAX_COL || row < 0 || row >= MAX_ROW) throw new FormulaError("#NAME?");
}

export function toNumber(v: Value): number {
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  const n = Number(v);
  if (Number.isNaN(n)) throw new FormulaError("#VALUE!");
  return n;
}

export function toBoolean(v: Value): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  throw new FormulaError("#VALUE!");
}

function makeArgs(exprs: Expr[], ctx: EvalContext): Arg[] {
  return exprs.map((argExpr) => ({
    eval: () => evaluate(argExpr, ctx),
    values: () => flatten(argExpr, ctx),
  }));
}

function makeFnContext(ctx: EvalContext): FnContext {
  return {
    external: (key) => {
      ctx.trackExternal?.(key);
      return ctx.resolveExternal ? ctx.resolveExternal(key) : null;
    },
  };
}

function flatten(ast: Expr, ctx: EvalContext): Value[] {
  if (ast.kind === "call") {
    const def = ctx.functions[ast.name.toUpperCase()];
    if (def?.values) {
      const out: Value[] = [];
      for (const v of def.values(makeArgs(ast.args, ctx), makeFnContext(ctx))) {
        if (v !== null) out.push(v);
      }
      return out;
    }
  }
  if (ast.kind === "ref" && ast.addr.end) {
    const { start, end } = ast.addr;
    checkBounds(start.col, start.row);
    checkBounds(end.col, end.row);
    const sheet = start.sheet ?? ctx.sheet;
    const out: Value[] = [];
    for (let col = Math.min(start.col, end.col); col <= Math.max(start.col, end.col); col++) {
      for (let row = Math.min(start.row, end.row); row <= Math.max(start.row, end.row); row++) {
        const v = ctx.resolveCell(sheet, col, row);
        if (v !== null) out.push(v);
      }
    }
    return out;
  }
  return [evaluate(ast, ctx)];
}

function evalBinary(op: BinaryOp, l: Value, r: Value): Value {
  switch (op) {
    case "+":
      return toNumber(l) + toNumber(r);
    case "-":
      return toNumber(l) - toNumber(r);
    case "*":
      return toNumber(l) * toNumber(r);
    case "/": {
      const d = toNumber(r);
      if (d === 0) throw new FormulaError("#DIV/0!");
      return toNumber(l) / d;
    }
    case "^":
      return toNumber(l) ** toNumber(r);
    case "=":
      return l === r;
    case "<>":
      return l !== r;
    case "<":
      return toNumber(l) < toNumber(r);
    case ">":
      return toNumber(l) > toNumber(r);
    case "<=":
      return toNumber(l) <= toNumber(r);
    case ">=":
      return toNumber(l) >= toNumber(r);
  }
}

export function evaluate(ast: Expr, ctx: EvalContext): Value {
  switch (ast.kind) {
    case "num":
    case "str":
    case "bool":
      return ast.value;
    case "err":
      throw new FormulaError(ast.code);
    case "unary": {
      const v = toNumber(evaluate(ast.operand, ctx));
      return ast.op === "-" ? -v : v;
    }
    case "binary":
      return evalBinary(ast.op, evaluate(ast.left, ctx), evaluate(ast.right, ctx));
    case "ref": {
      if (ast.addr.end) throw new FormulaError("#VALUE!");
      const { start } = ast.addr;
      checkBounds(start.col, start.row);
      return ctx.resolveCell(start.sheet ?? ctx.sheet, start.col, start.row) ?? 0;
    }
    case "call": {
      const def = ctx.functions[ast.name.toUpperCase()];
      if (!def) throw new FormulaError("#NAME?");
      return def.fn(makeArgs(ast.args, ctx), makeFnContext(ctx));
    }
  }
}
